// Servomotores en 3D: SG90 (cuerpo azul translúcido) y MG996R (negro, eje de metal), con sus orejas, el eje con
// brazo blanco y el cable con conector. El brazo puede llevar una carga (ligera, pesada o un bloqueo) y vibra
// cuando el motor empuja y no gira.
import * as THREE from 'three';
import { Builder, mat, M, canvas, texture, text } from '../kit.js';
import { servoModel } from '../../parts/servo.js';
import { PITCH } from '../../parts/registry.js';

// Aspecto de cada modelo: material del cuerpo, tapa (radio y alto del buje, bulto del potenciómetro), eje y
// tamaño del brazo respecto del SG90.
const STYLE = {
  SG90: { boss: [5.9, 3.8], bump: [2.6, 6.2, 2.0], spline: [2.35, 3.2], horn: 1, earHole: 1.1, wireY: 4.2 },
  MG996R: { boss: [7.0, 2.6], bump: [3.4, 11.4, 2.4], spline: [2.9, 3.8], horn: 1.45, earHole: 2.2, wireY: 8.0 },
};

function hornShape(kind, s = 1) {
  const arm = (len, w0, w1, angle) => {
    const c = Math.cos(angle);
    const sn = Math.sin(angle);
    return [[0, -w0 / 2], [len, -w1 / 2], [len, w1 / 2], [0, w0 / 2]].map(([x, y]) => [x * c - y * sn, x * sn + y * c]);
  };
  const arms = kind === 'cross' ? [0, Math.PI / 2, Math.PI, -Math.PI / 2] : kind === 'double' ? [0, Math.PI] : [0];
  return arms.map((a) => {
    const p = arm((kind === 'cross' ? 11 : 15.5) * s, 5.6 * s, 3.4 * s, a);
    const sh = new THREE.Shape();
    sh.moveTo(...p[0]);
    sh.lineTo(...p[1]);
    sh.absarc((p[1][0] + p[2][0]) / 2, (p[1][1] + p[2][1]) / 2, 1.7 * s, a - Math.PI / 2, a + Math.PI / 2, false);
    sh.lineTo(...p[3]);
    sh.lineTo(...p[0]);
    return sh;
  });
}

export function buildServo(def, props) {
  const model = servoModel(props);
  const st = STYLE[model.name];
  const big = model.name === 'MG996R';
  const b = new Builder();
  const { x0, x1, w, h } = model.body;
  const { ear } = model;
  const shell = big
    ? mat('#1f2126', { roughness: 0.38 })
    : new THREE.MeshPhysicalMaterial({ color: '#2a5fc6', roughness: 0.35, metalness: 0, transmission: 0, transparent: true, opacity: 0.93, clearcoat: 0.6 });
  const lowerH = big ? ear.y + ear.t : h - 4; // el SG90 lo hace 0.3 mm más alto que la cara de las orejas
  b.rbox(x1 - x0, lowerH, w, big ? 1.8 : 1.2, shell, 0, 0, 0);
  b.box(ear.len, ear.t, w, shell, 0, ear.y, 0);
  b.rbox(x1 - x0, h - lowerH, w, 1.0, shell, 0, lowerH, 0);
  for (const x of [-1, 1]) b.cyl(st.earHole, st.earHole, ear.t + 0.1, M.hole, x * (ear.len / 2 - (big ? 3.0 : 2.3)), ear.y - 0.05, 0, 12);
  b.cyl(st.boss[0], st.boss[0], st.boss[1], shell, model.shaftX, h, 0, 32);
  b.cyl(st.bump[0], st.bump[0], st.bump[2], shell, model.shaftX - st.bump[1], h, 0, 20);
  const splineY = h + st.boss[1];
  b.cyl(st.spline[0], st.spline[0], st.spline[1], big ? M.steel : mat('#f2f2ee', { roughness: 0.5 }), model.shaftX, splineY, 0, 20);
  // Etiqueta con el modelo en la cara frontal (+z)
  const label = canvas(18, 6, 24);
  text(label.g, model.name, 9, 3, { size: big ? 3.6 : 3.2, color: big ? '#d9dce1' : '#f2f6ff', weight: 800 });
  const sticker = new THREE.Mesh(new THREE.PlaneGeometry(18, 6), new THREE.MeshStandardMaterial({ map: texture(label.c), transparent: true, roughness: 0.5 }));
  sticker.position.set(big ? -6 : -2.5, lowerH * (big ? 0.52 : 0.5), w / 2 + 0.05);
  sticker.userData.noPick = true;
  b.root.add(sticker);
  // Cable plano (café, rojo, naranja) hasta el conector
  const colors = ['#e88a1a', '#d23a2c', '#5b3a24'];
  const cx = model.connectorX;
  for (let i = 0; i < 3; i++) {
    const z = (i - 1) * (big ? 1.6 : 1.05);
    const zc = (i - 1) * PITCH;
    const pts = [new THREE.Vector3(x0 + 0.5, st.wireY, z), new THREE.Vector3(x0 - 6, st.wireY - 0.6, z), new THREE.Vector3(cx + 20, 1.6, zc * 0.7), new THREE.Vector3(cx + 14.5, 1.3, zc)];
    const curve = new THREE.CatmullRomCurve3(pts);
    b.add(new THREE.TubeGeometry(curve, 24, big ? 0.7 : 0.55, 8, false), mat(colors[i], { roughness: 0.45 }));
  }
  // Conector hembra de 3 vías
  b.box(14.5, 2.6, 3 * PITCH + 0.6, M.blackPlastic, cx + 7.25, 0, 0);
  for (let i = 0; i < 3; i++) b.box(0.2, 1.2, 1.2, M.hole, cx - 0.05, 0.7, (i - 1) * PITCH);
  b.build();

  // Brazo (gira con el ángulo)
  const horn = new THREE.Group();
  horn.position.set(model.shaftX, splineY + st.spline[1], 0);
  const white = mat('#f4f4f0', { roughness: 0.55 });
  for (const sh of hornShape(props.horn || 'single', st.horn)) {
    const g = new THREE.ExtrudeGeometry(sh, { depth: 1.6, bevelEnabled: false, curveSegments: 10 });
    g.rotateX(-Math.PI / 2);
    const m = new THREE.Mesh(g, white);
    m.castShadow = true;
    horn.add(m);
  }
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(3.6 * st.horn, 3.6 * st.horn, 3.2, 24), white);
  hub.position.y = -0.9;
  hub.castShadow = true;
  horn.add(hub);
  for (let i = 1; i <= 4; i++) {
    const hole = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 1.8, 8), M.hole);
    hole.position.set((3.2 + i * 2.6) * st.horn, 0.8, 0);
    horn.add(hole);
  }
  b.root.add(horn);

  // Cargas que se ven en la punta del brazo: una pesa de latón, una de acero y un tope rojo que lo bloquea.
  const tip = (props.horn === 'cross' ? 11 : 15.5) * st.horn - 2.4;
  const loads = {
    light: new THREE.Mesh(new THREE.CylinderGeometry(2.6, 2.6, 4.5, 20), mat('#c9983f', { roughness: 0.3, metalness: 0.9 })),
    heavy: new THREE.Mesh(new THREE.BoxGeometry(8.5, 7.5, 8.5), mat('#8f959c', { roughness: 0.3, metalness: 0.95 })),
    blocked: new THREE.Mesh(new THREE.BoxGeometry(5, 9, 11), mat('#d6322a', { roughness: 0.45 })),
  };
  loads.light.position.set(tip, 1.6 + 2.25, 0);
  loads.heavy.position.set(tip, 1.6 + 3.75, 0);
  loads.blocked.position.set(tip + 0.2, 1.6 + 4.5, 0);
  for (const m of Object.values(loads)) {
    m.castShadow = true;
    m.visible = false;
    horn.add(m);
  }
  const showLoad = (name) => {
    for (const [k, m] of Object.entries(loads)) m.visible = k === name;
  };
  showLoad(props.load);

  let angle = 90;
  let shown = 90;
  let stalled = false;
  let time = 0;
  const apply = () => {
    // Atascado, el motor zumba: el brazo tiembla una fracción de grado.
    const shake = stalled ? Math.sin(time * 95) * 0.9 : 0;
    horn.rotation.y = ((shown + shake) * Math.PI) / 180;
  };
  apply();
  return {
    root: b.root,
    update(v) {
      angle = v.angle;
      stalled = !!v.stalled;
    },
    setProps(p) {
      showLoad(p.load);
    },
    animate(dt) {
      time += dt;
      // Suaviza entre cuadros de la simulación
      const d = angle - shown;
      if (Math.abs(d) < 0.05) shown = angle;
      else shown += d * Math.min(1, dt * 25);
      apply();
      return stalled || shown !== angle;
    },
    reset() {
      stalled = false;
      apply();
    },
  };
}

export const MODELS = { servo: buildServo };
